import React, { useState, useEffect } from "react";
import { useLanguage } from '../context/LanguageContext';
import adminApi from "../adminApi";
import { backendUrl } from "../../backendUrl";
import "./DatabasePreview.css";

interface TableData {
  data: any[];
  total: number;
}

function DatabasePreview() {
  const { t } = useLanguage();
  const [tables, setTables] = useState<string[]>([]);
  const [activeTable, setActiveTable] = useState<string | null>(null);
  const [tableData, setTableData] = useState<TableData>({ data: [], total: 0 });
  const [primaryKey, setPrimaryKey] = useState<string | null>(null);
  const [limit] = useState<number>(100);
  const [offset, setOffset] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editCell, setEditCell] = useState<{ row: number; column: string } | null>(null);
  const [editValue, setEditValue] = useState("");

  // Row edit state
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  const [editingRowData, setEditingRowData] = useState<Record<string, any> | null>(null);

  useEffect(() => {
    fetchTables();
  }, []);

  // Refetch table data when active table or offset changes
  useEffect(() => {
    if (activeTable) {
      fetchTableData(activeTable, limit, offset);
    }
  }, [activeTable, offset, limit]);

  const fetchTables = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get("/database/tables");
      setTables(response.data);
      if (response.data.length > 0) {
        setActiveTable(response.data[0]);
        setOffset(0); // reset pagination when loading tables
      }
      setError(null);
    } catch (err) {
      console.error("Error fetching tables:", err);
      setError(t("Failed to load database tables"));
    } finally {
      setLoading(false);
    }
  };

  // try to detect a primary key column name from returned rows
  const detectPrimaryKey = (rows: any[]): string | null => {
    if (!rows || rows.length === 0) return null;
    const keys = Object.keys(rows[0] || {});
    // prefer exact 'id' (case-insensitive), then columns that endWith '_id', then first key named like 'ID' or similar
    const lowerKeys = keys.map(k => k.toLowerCase());
    const idxId = lowerKeys.indexOf("id");
    if (idxId >= 0) return keys[idxId];
    const idxUnderscoreId = lowerKeys.findIndex(k => k.endsWith("_id"));
    if (idxUnderscoreId >= 0) return keys[idxUnderscoreId];
    // fallback: common variations
    const variants = ["Id", "ID", "pk", "pk_id"];
    for (const v of variants) {
      const i = keys.indexOf(v);
      if (i >= 0) return keys[i];
    }
    // last resort: first key
    return keys[0] ?? null;
  };

  const fetchTableData = async (tableName: string, limit = 100, offset = 0) => {
    try {
      setLoading(true);
      const response = await adminApi.get(
        `/database/tables/${tableName}`,
        { params: { limit, offset } }
      );
      setTableData(response.data);
      // detect primary key from returned rows
      const pk = detectPrimaryKey(response.data.data || []);
      setPrimaryKey(pk);
      setError(null);
    } catch (err) {
      console.error("Error fetching table data:", err);
      setError(t("Failed to load table data"));
      setPrimaryKey(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevPage = () => {
    setOffset((prev) => Math.max(0, prev - limit));
  };

  const handleNextPage = () => {
    setOffset((prev) => prev + limit);
  };

  const handleCellEdit = (rowIndex: number, column: string, currentValue: any) => {
    setEditCell({ row: rowIndex, column });
    setEditValue(currentValue?.toString() || "");
  };

  const handleSaveCell = async () => {
    if (!editCell || !activeTable) return;
    const row = tableData.data[editCell.row];
    // use the first column's name and value as the WHERE key
    const firstKey = Object.keys(row || {})[0];
    const idVal = firstKey ? row[firstKey] : undefined;
    if (!row || !firstKey || idVal === undefined || idVal === null) {
      alert(t("Cannot edit row: missing first-column primary value"));
      setEditCell(null);
      return;
    }

    try {
      await adminApi.put(
        `/database/tables/${activeTable}`,
        {
          id: idVal,
          column: editCell.column,
          value: editValue,
          pkName: firstKey,
        }
      );

      // Odśwież dane tabeli
      await fetchTableData(activeTable, limit, offset);
      setEditCell(null);
    } catch (err) {
      console.error("Error updating cell:", err);
      setError(t("Failed to update cell"));
    }
  };

  const handleCancelEdit = () => {
    setEditCell(null);
    setEditValue("");
  };

  const handleDeleteRow = async (rowIndex: number) => {
    if (!activeTable) return;
    const row = tableData.data[rowIndex];
    const firstKey = Object.keys(row || {})[0];
    const idVal = firstKey ? row[firstKey] : undefined;

    if (!row || !firstKey || idVal === undefined || idVal === null) {
      alert(t("Cannot delete row: missing primary identifier"));
      return;
    }

    if (!window.confirm(t("Are you sure you want to delete this row?"))) return;

    try {
      setLoading(true);
      await adminApi.delete(`/database/tables/${activeTable}/rows`, {
        data: { id: idVal, pkName: firstKey }
      });
      await fetchTableData(activeTable, limit, offset);
    } catch (err) {
      console.error("Error deleting row:", err);
      setError(t("Failed to delete row"));
    } finally {
      setLoading(false);
    }
  };

  const handleClearTable = async () => {
    if (!activeTable) return;
    if (!window.confirm(`${t("PERMANENTLY DELETE ALL DATA from table")} "${activeTable}"? ${t("This cannot be undone.")}`)) return;

    try {
      setLoading(true);
      await adminApi.delete(`/database/tables/${activeTable}/clear`);
      setOffset(0);
      await fetchTableData(activeTable, limit, 0);
    } catch (err) {
      console.error("Error clearing table:", err);
      setError(t("Failed to clear table"));
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    if (!activeTable) return;
    try {
      setLoading(true);
      const response = await adminApi.get(`/database/tables/${activeTable}/export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${activeTable}_export_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting CSV:", err);
      alert(t("Failed to export CSV. Make sure you are logged in."));
    } finally {
      setLoading(false);
    }
  };

  // Row edit handlers
  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    setEditingRowData({ ...(tableData.data[rowIndex] || {}) });
  };

  const handleChangeRowField = (field: string, value: any) => {
    setEditingRowData((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleSaveRow = async () => {
    if (editingRowIndex === null || !activeTable || !editingRowData) return;

    const originalRow = tableData.data[editingRowIndex];
    // use the first column as the identifying key
    const firstKey = Object.keys(originalRow || {})[0];
    const idVal = firstKey ? originalRow[firstKey] : undefined;
    if (!originalRow || !firstKey || idVal === undefined || idVal === null) {
      alert(t("Cannot edit row: missing first-column primary value"));
      setEditingRowIndex(null);
      setEditingRowData(null);
      return;
    }

    // Determine changed fields
    const changedFields = Object.keys(editingRowData).filter(
      (k) => String(editingRowData[k]) !== String(originalRow[k])
    );

    if (changedFields.length === 0) {
      setEditingRowIndex(null);
      setEditingRowData(null);
      return;
    }

    try {
      setLoading(true);
      // Send individual column updates (backend expects id/column/value)
      await Promise.all(
        changedFields.map((col) =>
          adminApi.put(`/database/tables/${activeTable}`, {
            id: idVal,
            column: col,
            value: editingRowData[col],
            pkName: firstKey,
          })
        )
      );
      await fetchTableData(activeTable, limit, offset);
      setEditingRowIndex(null);
      setEditingRowData(null);
    } catch (err) {
      console.error("Error saving row:", err);
      setError(t("Failed to save row"));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRow = () => {
    setEditingRowIndex(null);
    setEditingRowData(null);
  };

  const renderTableContent = () => {
    if (loading) {
      return <div className="loading">{t("Loading table data...")}</div>;
    }

    if (!activeTable || tableData.data.length === 0) {
      return (
        <div className="empty-state">
          <p>{t("No data available in this table")}</p>
        </div>
      );
    }

    const columns = Object.keys(tableData.data[0]);

    return (
      <div className="table-wrapper" style={{ overflowX: 'auto', display: 'block', width: '100%' }}>
        <table className="data-table" style={{ minWidth: 'max-content' }}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col}>{col}</th>
              ))}
              <th>{t("Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {tableData.data.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {columns.map((col) => (
                  <td key={col}>
                    {editingRowIndex === rowIndex ? (
                      <input
                        type="text"
                        value={editingRowData ? String(editingRowData[col] ?? "") : ""}
                        onChange={(e) => handleChangeRowField(col, e.target.value)}
                        title={`${t("Edit")} ${col}`}
                      />
                    ) : editCell?.row === rowIndex && editCell?.column === col ? (
                      <div className="edit-cell">
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") handleSaveCell();
                            if (e.key === "Escape") handleCancelEdit();
                          }}
                          autoFocus
                        />
                        <button className="btn-save-cell" onClick={handleSaveCell}>
                          ✓
                        </button>
                        <button className="btn-cancel-cell" onClick={handleCancelEdit}>
                          ✗
                        </button>
                      </div>
                    ) : (
                      <span
                        className="cell-content"
                        onDoubleClick={() => handleCellEdit(rowIndex, col, row[col])}
                        title={t("Double-click to edit")}
                      >
                        {typeof row[col] === "boolean"
                          ? row[col]
                            ? "✔"
                            : "—"
                          : row[col]?.toString() || "—"}
                      </span>
                    )}
                  </td>
                ))}
                <td>
                  {editingRowIndex === rowIndex ? (
                    <>
                      <button className="btn-save-row" onClick={handleSaveRow}>
                        {t("Save")}
                      </button>
                      <button className="btn-cancel-row" onClick={handleCancelRow}>{t("Cancel")}
                      </button>
                    </>
                  ) : (
                    <div className="row-actions">
                      <button className="btn-edit-row" onClick={() => handleEditRow(rowIndex)}>
                        {t("Edit")}
                      </button>
                      <button className="btn-delete-row" onClick={() => handleDeleteRow(rowIndex)} title={t("Delete row")}>
                        {t("Delete")}
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="pagination-controls">
          <button className="btn-prev" onClick={handlePrevPage} disabled={offset === 0}>
            ◀ {t("Prev")}
          </button>
          <span className="pagination-info">
            {t("Showing")} {Math.min(offset + 1, tableData.total)} -{" "}
            {Math.min(offset + tableData.data.length, tableData.total)} {t("of")} {tableData.total}
          </span>
          <button
            className="btn-next"
            onClick={handleNextPage}
            disabled={offset + tableData.data.length >= tableData.total}
          >
            {t("Next")} ▶
          </button>
        </div>
        <div className="table-info">
          <span>{t("Showing")} {tableData.data.length} {t("rows on this page — total")} {tableData.total}</span>
          <div className="table-info-actions">
            <button className="btn-export-csv" onClick={handleExportCSV}>
              {t("Export to CSV")}
            </button>
            <button className="btn-clear-table" onClick={handleClearTable}>
              {t("Clear Table")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (error && !loading) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="db-preview-container">
      <h2 className="db-preview-title">{t("Database Preview & Edit")}</h2>
      <p className="db-preview-subtitle">
        {t("View and edit database tables. Double-click any cell to edit its value.")}
      </p>

      <div className="tab-navigation">
        {tables.map((table) => (
          <button
            key={table}
            className={`tab-button ${activeTable === table ? "active" : ""}`}
            onClick={() => setActiveTable(table)}
          >
            {table}
          </button>
        ))}
      </div>

      {renderTableContent()}
    </div>
  );
}

export default DatabasePreview;